import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header";
import VertNav from "../VertNav";
import {
    FaCalendarAlt,
    FaClock,
    FaUserCheck,
    FaUserTimes,
    FaUsers,
    FaSearch,
    FaFilter,
    FaDownload,
    FaEye,
    FaMapMarkerAlt,
    FaCamera,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../../../config";

function AttendanceDashboard() {
    const [attendanceData, setAttendanceData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(
        new Date().toISOString().split("T")[0]
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [showDetails, setShowDetails] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");
    const [dashboardSummary, setDashboardSummary] = useState({
        total_staff: 0,
        present_count: 0,
        absent_count: 0,
        late_arrival_count: 0,
        attendance_percentage: 0,
        expected_in_time: "09:00",
    });

    const sname = localStorage.getItem("sname");
    const branchName = localStorage.getItem("branch_name");
    const bid = localStorage.getItem("branch_id");
    const navigate = useNavigate();

    const fetchAttendanceData = async (date = selectedDate) => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(
                `${config.apiUrl}/api/swalook/attendance/details/?branch_name=${bid}&date=${date}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();

            if (data.status) {
                // Transform API data to match our component structure
                const transformedData = data.attendance_data.map(
                    (employee) => ({
                        id: employee.staff_id,
                        employee: `${employee.staff_name} (${
                            employee.staff_role || "Staff"
                        })`,
                        number: employee.mobile_number,
                        date: formatDate(date),
                        inTime:
                            employee.checkin_time !== "--"
                                ? formatTime(employee.checkin_time)
                                : "--",
                        outTime:
                            employee.checkout_time !== "--"
                                ? formatTime(employee.checkout_time)
                                : "--",
                        totalHours: calculateTotalHours(
                            employee.checkin_time,
                            employee.checkout_time
                        ),
                        status: employee.status,
                        location:
                            employee.location !== "--"
                                ? employee.location
                                : "-",
                        selfie: employee.has_selfie
                            ? "available"
                            : "not available",
                        selfieImage: employee.selfie_url || "",
                        remarks: employee.remarks || "/",
                        status_color: employee.status_color,
                        designation: employee.designation || "Staff",
                    })
                );

                setAttendanceData(transformedData);
                setFilteredData(transformedData);
                setDashboardSummary(data.dashboard_summary);
            } else {
                throw new Error(
                    data.message || "Failed to fetch attendance data"
                );
            }
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            toast.error("Failed to load attendance data");
            // Fallback to empty data
            setAttendanceData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date
            .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            })
            .replace(/\//g, ".");
    };

    // Helper function to format time
    const formatTime = (timeString) => {
        if (!timeString || timeString === "--") return "--";

        const time = new Date(`2000-01-01T${timeString}`);
        return time.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    // Helper function to calculate total hours
    const calculateTotalHours = (checkinTime, checkoutTime) => {
        if (checkinTime === "--" || checkoutTime === "--") return "0:00";

        try {
            const checkin = new Date(`2000-01-01T${checkinTime}`);
            const checkout = new Date(`2000-01-01T${checkoutTime}`);

            const diffMs = checkout - checkin;
            const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
            const diffMins = Math.floor(
                (diffMs % (1000 * 60 * 60)) / (1000 * 60)
            );

            return `${diffHrs}:${diffMins.toString().padStart(2, "0")}`;
        } catch (error) {
            console.error("Error calculating total hours:", error);
            return "0:00";
        }
    };

    useEffect(() => {
        fetchAttendanceData();
    }, [bid]);

    useEffect(() => {
        fetchAttendanceData(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        let filtered = attendanceData;

        // Apply search filter
        if (searchQuery) {
            filtered = filtered.filter(
                (emp) =>
                    emp.employee
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                    emp.number.includes(searchQuery)
            );
        }

        // Apply status filter
        if (statusFilter !== "all") {
            filtered = filtered.filter(
                (emp) => emp.status.toLowerCase() === statusFilter.toLowerCase()
            );
        }

        setFilteredData(filtered);
    }, [searchQuery, statusFilter, attendanceData]);

    const handleViewDetails = (employee) => {
        setSelectedEmployee(employee);
        setShowDetails(true);
    };

    const handleExportData = () => {
        // Implement export functionality
        toast.success("Attendance data exported successfully!");
    };

    const handleAllAttendance = () => {
        navigate(`/${sname}/${branchName}/view-all-attendance`);
    };

    const handleViewImage = (imageUrl) => {
        if (imageUrl) {
            setSelectedImage(imageUrl);
            setShowImageModal(true);
        } else {
            toast.warning("No selfie image available");
        }
    };

    const handleDownloadImage = (imageUrl, employeeName) => {
        if (!imageUrl) {
            toast.warning("No selfie image available to download");
            return;
        }

        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `selfie-${
            employeeName.split(" ")[0]
        }-${selectedDate}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Selfie image downloaded!");
    };

    const StatsCard = ({ title, value, icon, color }) => (
        <div className="bg-white p-6 rounded-2xl shadow-sm flex flex-col items-center justify-center hover:shadow-md transition-shadow">
            <div className={`p-3 rounded-full ${color} mb-3`}>{icon}</div>
            <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
            <p className="text-2xl font-bold text-gray-800 mt-2">{value}</p>
        </div>
    );

    const StatusBadge = ({ status }) => {
        const getStatusColor = (status) => {
            switch (status.toLowerCase()) {
                case "present":
                    return "bg-green-100 text-green-800";
                case "absent":
                    return "bg-red-100 text-red-800";
                case "late":
                    return "bg-yellow-100 text-yellow-800";
                default:
                    return "bg-gray-100 text-gray-800";
            }
        };

        return (
            <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    status
                )}`}
            >
                {status}
            </span>
        );
    };

    return (
        <>
            <div className="bg-gray-100 min-h-screen">
                <Header />
                <VertNav />
                <div className="md:ml-72 p-6">
                    {/* Stats Section */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        <StatsCard
                            title="Total Employees"
                            value={dashboardSummary.total_staff}
                            icon={<FaUsers className="text-white text-xl" />}
                            color="bg-blue-500"
                        />
                        <StatsCard
                            title="Present Today"
                            value={dashboardSummary.present_count}
                            icon={
                                <FaUserCheck className="text-white text-xl" />
                            }
                            color="bg-green-500"
                        />
                        <StatsCard
                            title="Absent Today"
                            value={dashboardSummary.absent_count}
                            icon={
                                <FaUserTimes className="text-white text-xl" />
                            }
                            color="bg-red-500"
                        />
                        <StatsCard
                            title="Late Arrivals"
                            value={dashboardSummary.late_arrival_count}
                            icon={<FaClock className="text-white text-xl" />}
                            color="bg-yellow-500"
                        />
                    </div>

                    {/* Main Content */}
                    <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                                <FaCalendarAlt className="mr-2 text-blue-500" />
                                Attendance Dashboard
                            </h2>

                            {/* <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                                <button
                                    onClick={handleAllAttendance}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition"
                                >
                                    View All Records
                                </button>
                                <button
                                    onClick={handleExportData}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center transition"
                                >
                                    <FaDownload className="mr-2" />
                                    Export
                                </button>
                            </div> */}
                        </div>

                        {/* Filters Section */}
                        <div className="bg-gray-50 p-6 rounded-xl mb-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                <FaFilter className="mr-2 text-blue-500" />
                                Filters & Search
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Date Filter */}
                                <div className="flex flex-col space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        Select Date
                                    </label>
                                    <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) =>
                                            setSelectedDate(e.target.value)
                                        }
                                        className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                    />
                                </div>

                                {/* Search */}
                                <div className="flex flex-col space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        Search Employee
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-3 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search by employee name or number..."
                                            value={searchQuery}
                                            onChange={(e) =>
                                                setSearchQuery(e.target.value)
                                            }
                                            className="border border-gray-300 rounded-lg pl-10 pr-4 py-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                        />
                                    </div>
                                </div>

                                {/* Status Filter */}
                                <div className="flex flex-col space-y-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        Filter by Status
                                    </label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) =>
                                            setStatusFilter(e.target.value)
                                        }
                                        className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                    >
                                        <option value="all">All Status</option>
                                        <option value="present">Present</option>
                                        <option value="absent">Absent</option>
                                        <option value="late">Late</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Attendance Table */}
                        <div className="bg-gray-50 p-6 rounded-xl">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                                Today's Attendance ({formatDate(selectedDate)})
                            </h3>

                            {loading ? (
                                <div className="flex justify-center items-center h-32">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Employee
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Mobile No.
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    In-Time
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Out-Time
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Total Hours
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredData.length > 0 ? (
                                                filteredData.map((record) => (
                                                    <tr
                                                        key={record.id}
                                                        className="hover:bg-gray-50"
                                                    >
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {record.employee}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {record.number}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {record.inTime}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            {record.outTime}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-semibold">
                                                            {record.totalHours}
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                            <StatusBadge
                                                                status={
                                                                    record.status
                                                                }
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                            <button
                                                                onClick={() =>
                                                                    handleViewDetails(
                                                                        record
                                                                    )
                                                                }
                                                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                                                            >
                                                                <FaEye className="mr-1" />
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td
                                                        colSpan="7"
                                                        className="px-4 py-4 text-center text-sm text-gray-500"
                                                    >
                                                        No attendance records
                                                        found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Employee Details Modal */}
            {showDetails && selectedEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="flex justify-between items-center border-b p-4">
                            <h2 className="text-xl font-bold text-gray-800">
                                Attendance Details
                            </h2>
                            <button
                                onClick={() => setShowDetails(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto flex-grow">
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            Employee
                                        </label>
                                        <p className="text-lg font-semibold text-gray-900">
                                            {selectedEmployee.employee}
                                            <br />
                                            {selectedEmployee.number}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            Date
                                        </label>
                                        <p className="text-lg text-gray-900">
                                            {selectedEmployee.date}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            In-Time
                                        </label>
                                        <p className="text-lg font-semibold text-gray-900">
                                            {selectedEmployee.inTime}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            Out-Time
                                        </label>
                                        <p className="text-lg font-semibold text-gray-900">
                                            {selectedEmployee.outTime}
                                        </p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            Total Hours
                                        </label>
                                        <p className="text-lg font-bold text-blue-600">
                                            {selectedEmployee.totalHours}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">
                                            Status
                                        </label>
                                        <div className="mt-1">
                                            <StatusBadge
                                                status={selectedEmployee.status}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700 flex items-center">
                                        <FaMapMarkerAlt className="mr-2 text-gray-400" />
                                        Location
                                    </label>
                                    <p className="text-gray-900 mt-1">
                                        {selectedEmployee.location}
                                    </p>
                                </div>

                                {/* Selfie Verification Section */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 flex items-center">
                                        <FaCamera className="mr-2 text-gray-400" />
                                        Selfie Verification
                                    </label>
                                    <div className="mt-2">
                                        {selectedEmployee.selfie ===
                                            "available" &&
                                        selectedEmployee.selfieImage ? (
                                            <div className="flex flex-col space-y-3">
                                                <div className="relative group">
                                                    <div className="w-40 h-40 border-2 border-green-500 rounded-lg overflow-hidden shadow-md">
                                                        <img
                                                            src={
                                                                selectedEmployee.selfieImage
                                                            }
                                                            alt="Selfie Verification"
                                                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 cursor-pointer"
                                                            onClick={() =>
                                                                handleViewImage(
                                                                    selectedEmployee.selfieImage
                                                                )
                                                            }
                                                            onError={(e) => {
                                                                e.target.src =
                                                                    "https://via.placeholder.com/160x160/4CAF50/FFFFFF?text=Selfie+Image";
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 rounded-lg flex items-center justify-center">
                                                        <button
                                                            onClick={() =>
                                                                handleViewImage(
                                                                    selectedEmployee.selfieImage
                                                                )
                                                            }
                                                            className="opacity-0 group-hover:opacity-100 bg-white bg-opacity-90 rounded-full p-2 transition-all duration-200 transform scale-90 group-hover:scale-100"
                                                        >
                                                            <FaEye className="text-gray-700" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <p className="text-green-600 text-sm font-medium">
                                                            Verified with Selfie
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() =>
                                                            handleViewImage(
                                                                selectedEmployee.selfieImage
                                                            )
                                                        }
                                                        className="text-blue-600 hover:text-blue-800 text-xs flex items-center px-3 py-1 border border-blue-300 rounded-md"
                                                    >
                                                        <FaEye className="mr-1" />
                                                        View Full Size
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            handleDownloadImage(
                                                                selectedEmployee.selfieImage,
                                                                selectedEmployee.employee
                                                            )
                                                        }
                                                        className="text-green-600 hover:text-green-800 text-xs flex items-center px-3 py-1 border border-green-300 rounded-md"
                                                    >
                                                        <FaDownload className="mr-1" />
                                                        Download
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center space-y-2 p-4 border-2 border-dashed border-red-300 rounded-lg bg-red-50">
                                                <div className="w-16 h-16 border-2 border-red-300 rounded-full flex items-center justify-center bg-white">
                                                    <FaCamera className="text-red-400 text-2xl" />
                                                </div>
                                                <p className="text-red-600 font-medium text-center">
                                                    Selfie Not Available
                                                </p>
                                                <p className="text-red-500 text-xs text-center">
                                                    No selfie verification for
                                                    this attendance record
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-gray-700">
                                        Remarks
                                    </label>
                                    <p className="text-gray-900 mt-1">
                                        {selectedEmployee.remarks}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="border-t p-4 bg-gray-50">
                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowDetails(false)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image View Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="relative max-w-4xl max-h-full">
                        <img
                            src={selectedImage}
                            alt="Selfie Full View"
                            className="max-w-full max-h-full object-contain rounded-lg"
                            onError={(e) => {
                                e.target.src =
                                    "https://via.placeholder.com/400x400/4CAF50/FFFFFF?text=Image+Not+Found";
                            }}
                        />
                        <button
                            onClick={() => setShowImageModal(false)}
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition"
                        >
                            ✕
                        </button>
                        <button
                            onClick={() =>
                                handleDownloadImage(
                                    selectedImage,
                                    selectedEmployee?.employee || "employee"
                                )
                            }
                            className="absolute top-4 right-16 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition"
                        >
                            <FaDownload />
                        </button>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
}

export default AttendanceDashboard;
